import React, { useState } from "react";
import { FormattedMessage } from "react-intl";
import { Link, useNavigate } from "react-router";
import { Routes } from "constants/routes";
import axios from "axios";
import { API_BASE_URL, API_URLS, LOCALSTORAGE_TOKEN_ITEM } from "constants/api";
import { SmartForm } from "ui-kit/SmartForm";
import { Button } from "ui-kit/Button";
import styled from "styled-components";
import { Layout } from "../Layout";
import { LoginSignUpToggle } from "../components/LoginSignUpToggle";
import { Title } from "../components/Title";
import { SocialNetworks } from "../components/SocialNetworks";
import { LoginPassword } from "../components/LoginPassword";
import { Step } from "../SignUp/constants";
import {
  compose,
  hasRequiredLength,
  hasUpperCase,
  hasValidEmail,
  isShowInputError,
  structuredValidator
} from "utils/validation";
import {toast} from "react-toastify";

interface FormValues {
  email: string;
  password: string;
}

const MIN_PASSWORD_LENGTH = 8;

export const SignIn: React.FC = () => {
  const navigate = useNavigate();

  const onSubmit = async (values: FormValues) => {
    try {
      const response = await axios.post(`${API_BASE_URL}${API_URLS.SignIn}`, {
        email: values.email,
        password: values.password,
      });

      const responseData = response?.data;

      if (responseData) {
        const {data, success} = responseData;

        if (success && data) {
          localStorage.setItem(LOCALSTORAGE_TOKEN_ITEM, data.token);
          navigate(Routes.General);
        }
      }
    } catch (e: any) {
      const message = e?.response?.data?.message || e?.message;
      console.error("SignIn error:", e);
      toast.error(message);
    }
  };

  return (
    <Layout>
      <SmartForm<FormValues>
        onSubmit={onSubmit}
        initialValues={{ email: '', password: '' }}
        validate={structuredValidator({
          email: hasValidEmail,
          password: compose(hasUpperCase, hasRequiredLength(MIN_PASSWORD_LENGTH)),
        })}
      >
        {(props) => {
          const { handleSubmit, errors, submitting } = props;
          return (
            <Container id="sign-in" onSubmit={handleSubmit}>
              <Title>
                <FormattedMessage id="Auth.LogIn.Title" defaultMessage="Log in"/>
              </Title>
              <SocialNetworks/>
              <LoginPassword
                step={Step.Form}
                errors={{
                  email: isShowInputError<FormValues>(props, 'email') && <FormattedMessage id={errors?.email} />,
                  password: isShowInputError<FormValues>(props, 'password') && <FormattedMessage id={errors?.password} values={{characters: MIN_PASSWORD_LENGTH}} />,
                }}
              />
              <LastBlock>
                <Button type="submit" form="sign-in" loading={submitting}>
                  <FormattedMessage id="Auth.LogInButton" defaultMessage="Log in"/>
                </Button>
                <StyledLink to={Routes.RecoveryPassword}>
                  <FormattedMessage id="Auth.LogIn.ForgotYourPassword" defaultMessage="Forgot your password?"/>
                </StyledLink>
    
                <LoginSignUpToggle
                  link={Routes.SignUp}
                  message={(
                    <FormattedMessage id="Auth.LogInSignUpMessage" defaultMessage="Don't have an account?"/>
                  )}
                  linkText={(
                    <FormattedMessage id="Auth.LogInSignUpLink" defaultMessage="Sign up for free"/>
                  )}
                />
              </LastBlock>
            </Container>
          );
        }}
      </SmartForm>
    </Layout>
  );
};

const Container = styled.form`
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 17px;
  padding: 54px;
  height: 100%;
  max-width: 548px;

  @media (max-width: 1200px) {
    max-width: 100%;
  }
`;

const StyledLink = styled(Link)`
  color: #6777FF;
  font-size: 13px;
`;

const LastBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 11px;
`;
